// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { Store } from "../Store";
// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
// Constants
import { HttpStatus } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { ViewDataFetchState } from "@ms/uno-constants/lib/local/ViewConstants";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
// Utilities
import { extractPrimaryViewIdFromRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
/**
 * ViewLifecycleStore manages the data fetching lifecycle for views in the Planner App.
 *
 * This store is responsible for:
 * - Tracking data fetch states across different views (MyDay, MyTasks, MyPlans, BasicPlan)
 * - Managing force fetch states to determine when views should bypass cache and fetch fresh data
 * - Handling delta sync failures that require refreshing all view data
 * - Publishing store change events when view states are updated
 * - Providing performance markers for view data fetch operations
 *
 * The store maintains two primary state collections:
 * 1. `viewDataFetchStates` - Current fetch status for each view (All, critical, none, etc.)
 * 2. `viewForceFetchStates` - Boolean flags indicating whether views need to force refresh data
 *
 * Force fetch behavior is triggered in scenarios such as:
 * - Initial app load in background mode
 * - Delta sync token expiration
 * - Navigation to views that haven't been loaded in the current session
 */ export class ViewLifecycleStore extends EventPublishingStore {
    get name() {
        return Store.ViewLifecycle;
    }
    getViewDataFetchState(viewId) {
        return this.viewDataFetchStates[toViewIdString(viewId)] ?? null;
    }
    getViewForceDataFetchState(viewId) {
        return this.viewForceFetchStates[toViewIdString(viewId)] ?? false;
    }
    /**
     * Handles actions dispatched to the store.
     * @param action The ViewLifecycleStoreAction to handle
     */ handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.NavigateToView:
                this.insertViewForceFetchState(action.routeChangeDescriptor.subRouteType, action.routeChangeDescriptor.subRoute, true);
                break;
            case ActionType.ViewDataFetchAction:
                storeChanged = this.handleViewDataFetchAction(action);
                break;
            case ActionType.ViewForceDataFetchAction:
                storeChanged = this.handleViewForceDataFetchAction(action);
                break;
            case ActionType.DeltaSyncFailedAction:
                this.handleDeltaSyncFeedFetchFailure(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    /**
     * Update the data fetch state for a view
     * @param action The view data fetch action
     */ handleViewDataFetchAction(action) {
        const current = this.getViewDataFetchState(action.viewId);
        if (current === null || action.state > current) {
            this.loggers.performanceMarker.mark(MarkerTag.ViewDataFetch, {
                ...action.extraData ?? {},
                viewName: action.viewId.name,
                state: ViewDataFetchState[action.state]
            });
            this.viewDataFetchStates[toViewIdString(action.viewId)] = action.state;
            return true;
        }
        return false;
    }
    /**
     * handles the ViewForceDataFetchAction action
     * @param action IViewForceDataFetchAction
     */ handleViewForceDataFetchAction(action) {
        const key = toViewIdString(action.viewId);
        /*
            if key does not exist simply add the entry, if it exists then don't override false values
            this is to handle race conditions between pre-fetcher and default route set (RS)
            possible cases include pre-fetch [success (PS) & failure (PF) and route set/change (RS) and they can happen in any sequence as below
            - PS -> RS : key will not exist as Prefetch success is not setting this anymore, RS will add an entry with shouldForceFetch as false
            - RS -> PS : key will exist via RS with shouldForceFetch: false, PS will not overwrite.
            - PF -> RS : On PF we send forceFetch = true to ViewAC & key will also be set here with shouldForceFetch: false, RS will not overwrite
            - RS -> PF : key will exist via RS with shouldForceFetch: false, On PF we send forceFetch = true to ViewAC and don't overwrite here.
        */ if (!(key in this.viewForceFetchStates) || this.viewForceFetchStates[key] !== false) {
            this.viewForceFetchStates[key] = action.shouldForceFetch;
            return true;
        }
        return false;
    }
    /**
     * Inserts a view force fetch state into the viewForceFetchStates object.
     */ insertViewForceFetchState(subRouteType, subRoute, shouldForceFetch) {
        // Add force fetch view states for the following sub route types only
        if (subRouteType !== SubRouteType.MyDay && subRouteType !== SubRouteType.MyTasks && subRouteType !== SubRouteType.MyPlans && subRouteType !== SubRouteType.BasicPlan) {
            return;
        }
        const key = toViewIdString(extractPrimaryViewIdFromRoute(subRouteType, subRoute, this.currentUserId));
        // If the key is valid and does not exist, add it with a default value of false
        if (key != null && !(key in this.viewForceFetchStates)) {
            this.viewForceFetchStates[key] = shouldForceFetch;
        }
    }
    /**
     * Set the force fetch states to true for all visited views in the session.
     */ handleDeltaSyncFeedFetchFailure(action) {
        const status = action.response?.status;
        // 499 is custom code for stale token, 410 is returned on graph
        if (status === 499 || status === HttpStatus.Gone) {
            // If the status is expired, we reset the view force fetch states
            for(const key in this.viewForceFetchStates){
                this.viewForceFetchStates[key] = true;
            }
        }
    }
    /**
     * Instantiate a new instance of ViewLifecycleStore
     * @param initialSubRouteType - The initial subroute type when the app loads
     * @param initialSubRoute - The initial subroute when the app loads
     * @param currentUserId - The id of the current user for view tracking
     * @param isBackgroundLoad - Whether the app was loaded in background mode (affects force fetch behavior)
     * @param loggers - Logger context for telemetry
     * @param configProvider - Configuration provider for feature flags and settings
     */ constructor(initialSubRouteType, initialSubRoute, currentUserId, isBackgroundLoad, loggers, configProvider){
        super(), this.initialSubRouteType = initialSubRouteType, this.initialSubRoute = initialSubRoute, this.currentUserId = currentUserId, this.isBackgroundLoad = isBackgroundLoad, this.loggers = loggers, this.configProvider = configProvider;
        this.viewDataFetchStates = {};
        this.viewForceFetchStates = {};
        // Add initial boot route to the view force fetch states.
        // If the app was loaded in background or the pre-fetch is disabled,
        // insert the state with force fetch to true which can be captured when the user opens the app
        const shouldForceFetchOnInitialRoute = this.isBackgroundLoad || this.configProvider().settings.disablePreFetcher;
        this.insertViewForceFetchState(this.initialSubRouteType, this.initialSubRoute, shouldForceFetchOnInitialRoute);
    }
}
